import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Calendar, RefreshCw, Send, Download, Eye, Clock,
  CheckCircle, AlertTriangle, TrendingUp, BarChart3, Bot
} from 'lucide-react';
import { aiAgent } from '../../../services/api/client';
import { toast } from '../../../utils/toastUtils';
import MarkdownRenderer from '../../../components/common/MarkdownRenderer';
import { useI18n } from '../../../i18n';

const AIAgentReports = () => {
  const { t } = useI18n();
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await aiAgent.getReports({ page, page_size: 10 });
      setReports(data.items || []);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      toast.error('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const fetchReportDetail = async (date) => {
    try {
      const detail = await aiAgent.getReportDetail(date);
      setSelectedReport(detail);
    } catch (err) {
      toast.error('Erreur lors du chargement du rapport');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await aiAgent.generateReport();
      toast.success('Report generated successfully');
      fetchReports();
    } catch (err) {
      toast.error('Error generating report');
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async (date, recipients) => {
    setSending(date);
    try {
      await aiAgent.sendReport(date, recipients);
      toast.success('Rapport envoye');
      fetchReports();
    } catch (err) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-400" />
            Daily Reports
          </h3>
          <p className="text-gray-400 text-sm mt-1">Daily summaries generated by the AI agent</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-2 px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/90"
          >
            {generating ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            Generer Maintenant
          </button>
          <button
            onClick={fetchReports}
            disabled={loading}
            className="p-2 bg-brand-black-lighter rounded-lg text-gray-400 hover:text-brand-green"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Reports List */}
        <div className="lg:col-span-1 space-y-3">
          <h4 className="text-gray-400 text-sm font-medium">Historique</h4>
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green"></div>
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun rapport disponible</p>
            </div>
          ) : (
            <>
              {reports.map((report) => (
                <div
                  key={report.id}
                  onClick={() => fetchReportDetail(report.report_date)}
                  className={`p-4 bg-brand-black rounded-xl border cursor-pointer transition-all ${
                    selectedReport?.report_date === report.report_date
                      ? 'border-brand-green/50'
                      : 'border-gray-800 hover:border-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-400" />
                      <span className="text-white font-medium">
                        {new Date(report.report_date).toLocaleDateString('fr-FR', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long'
                        })}
                      </span>
                    </div>
                    {report.sent_at && (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    )}
                  </div>
                  {report.summary && (
                    <p className="text-gray-400 text-sm line-clamp-2">{report.summary}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    Genere: {new Date(report.generated_at).toLocaleTimeString()}
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-2 py-1 bg-brand-black-lighter text-gray-400 rounded text-sm disabled:opacity-50"
                  >
                    Prec
                  </button>
                  <span className="text-gray-500 text-sm">{page}/{totalPages}</span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-2 py-1 bg-brand-black-lighter text-gray-400 rounded text-sm disabled:opacity-50"
                  >
                    Suiv
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Report Detail */}
        <div className="lg:col-span-2">
          {selectedReport ? (
            <div className="bg-brand-black rounded-xl border border-gray-800 p-6">
              {/* Report Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-800">
                <div>
                  <h4 className="text-xl font-bold text-white">
                    Rapport du {new Date(selectedReport.report_date).toLocaleDateString('fr-FR')}
                  </h4>
                  <p className="text-gray-500 text-sm mt-1">
                    Genere le {new Date(selectedReport.generated_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSend(selectedReport.report_date, selectedReport.sent_to || [])}
                    disabled={sending === selectedReport.report_date}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30"
                  >
                    <Send className="w-4 h-4" />
                    Envoyer
                  </button>
                </div>
              </div>

              {/* Summary */}
              {selectedReport.summary && (
                <div className="mb-6">
                  <h5 className="text-gray-400 text-sm font-medium mb-2 flex items-center gap-2">
                    <Bot className="w-4 h-4" /> AI Summary
                  </h5>
                  <div className="bg-brand-black-lighter p-4 rounded-lg">
                    <MarkdownRenderer content={selectedReport.summary} variant="compact" />
                  </div>
                </div>
              )}

              {/* Stats */}
              {selectedReport.stats && (
                <div className="mb-6">
                  <h5 className="text-gray-400 text-sm font-medium mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" /> Statistiques
                  </h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {selectedReport.stats.uptime_percent !== undefined && (
                      <div className="bg-brand-black-lighter p-3 rounded-lg text-center">
                        <p className="text-gray-500 text-xs">{t('settings.uptime')}</p>
                        <p className="text-lg font-bold text-green-400">
                          {selectedReport.stats.uptime_percent?.toFixed(1)}%
                        </p>
                      </div>
                    )}
                    {selectedReport.stats.total_scans !== undefined && (
                      <div className="bg-brand-black-lighter p-3 rounded-lg text-center">
                        <p className="text-gray-500 text-xs">Scans</p>
                        <p className="text-lg font-bold text-blue-400">
                          {selectedReport.stats.total_scans}
                        </p>
                      </div>
                    )}
                    {selectedReport.stats.incidents !== undefined && (
                      <div className="bg-brand-black-lighter p-3 rounded-lg text-center">
                        <p className="text-gray-500 text-xs">Incidents</p>
                        <p className="text-lg font-bold text-orange-400">
                          {typeof selectedReport.stats.incidents === 'object'
                            ? selectedReport.stats.incidents.total || 0
                            : selectedReport.stats.incidents}
                        </p>
                      </div>
                    )}
                    {selectedReport.stats.recoveries !== undefined && (
                      <div className="bg-brand-black-lighter p-3 rounded-lg text-center">
                        <p className="text-gray-500 text-xs">Recoveries</p>
                        <p className="text-lg font-bold text-purple-400">
                          {typeof selectedReport.stats.recoveries === 'object'
                            ? selectedReport.stats.recoveries.successful || 0
                            : selectedReport.stats.recoveries}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Incidents Summary */}
              {selectedReport.incidents_summary && selectedReport.incidents_summary.length > 0 && (
                <div className="mb-6">
                  <h5 className="text-gray-400 text-sm font-medium mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Incidents
                  </h5>
                  <div className="space-y-2">
                    {selectedReport.incidents_summary.map((incident, idx) => (
                      <div key={incident.id || `${incident.category}-${idx}`} className="flex items-center justify-between bg-brand-black-lighter p-3 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`w-2 h-2 rounded-full ${
                            incident.severity === 'critical' ? 'bg-red-500' :
                            incident.severity === 'warning' ? 'bg-orange-500' : 'bg-blue-500'
                          }`} />
                          <span className="text-white">{incident.title || incident.category}</span>
                        </div>
                        <span className="text-gray-500 text-sm">{incident.count || 1}x</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {selectedReport.recommendations && selectedReport.recommendations.length > 0 && (
                <div className="mb-6">
                  <h5 className="text-gray-400 text-sm font-medium mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" /> Recommandations
                  </h5>
                  <ul className="space-y-2">
                    {selectedReport.recommendations.map((rec, idx) => (
                      <li key={rec.id || `${rec.title || rec.text || ''}-${idx}`} className="flex items-start gap-2 text-white">
                        <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                          rec.priority === 'high' ? 'text-red-400' :
                          rec.priority === 'medium' ? 'text-orange-400' : 'text-brand-green'
                        }`} />
                        <div>
                          <span className="font-medium">{rec.title || rec.text || (typeof rec === 'string' ? rec : '')}</span>
                          {rec.description && (
                            <p className="text-gray-400 text-sm mt-0.5">{rec.description}</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Metadata */}
              <div className="pt-4 border-t border-gray-800 text-xs text-gray-500">
                <div className="flex flex-wrap gap-4">
                  {selectedReport.generation_time_ms && (
                    <span>Generation: {selectedReport.generation_time_ms}ms</span>
                  )}
                  {selectedReport.ai_tokens_used && (
                    <span>Tokens IA: {selectedReport.ai_tokens_used}</span>
                  )}
                  {selectedReport.sent_at && (
                    <span>Sent: {new Date(selectedReport.sent_at).toLocaleString()}</span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-brand-black rounded-xl border border-gray-800 p-12 text-center">
              <Eye className="w-12 h-12 mx-auto mb-3 text-gray-600" />
              <p className="text-gray-500">Select a report to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIAgentReports;
